/**
 * @file promotion-services.js
 * @description Core services for customer data and cart management
 * @module GniibPromotions.Services
 *
 * Contains CustomerService for customer identification and CartManager for
 * managing selected items and usage tracking.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};
  var Utils = GP.Utils;
  var Config = GP.Config;

  /**
   * Customer Service - handles customer identification and role detection
   * @namespace GniibPromotions.CustomerService
   */
  GP.CustomerService = {

    /**
     * Get customer ID from Shopify analytics or meta tags
     * @returns {string|null} Customer ID or null if not found
     */
    getCustomerId: function() {
      // Try to get customer ID from Shopify's customer object
      if (typeof window !== 'undefined' && window.ShopifyAnalytics) {
        return window.ShopifyAnalytics.meta.page.customerId;
      }

      // Fallback: try to get from meta tags
      var metaCustomerId = document.querySelector('meta[name="customer-id"]');
      if (metaCustomerId) {
        return metaCustomerId.content;
      }

      return null;
    },

    /**
     * Get customer role from URL parameter (liuyang only) or meta tags
     * @returns {string|null} Customer role or null if not found
     */
    getCustomerRole: function() {
      // Check URL parameter ONLY for liuyang promotion (public access via link)
      var urlParams = new URLSearchParams(window.location.search);
      var urlRole = urlParams.get('role');
      if (Utils.isUrlRoleAllowed(urlRole)) {
        return 'liuyang';
      }

      // For all other promotions, require role from meta tag (customer metafield)
      var metaCustomerRole = document.querySelector('meta[name="customer-role"]');
      if (metaCustomerRole && metaCustomerRole.content) {
        return metaCustomerRole.content.toLowerCase();
      }

      return null;
    },

    /**
     * Get customer tags from meta tags
     * @returns {string[]} Array of customer tags
     */
    getCustomerTags: function() {
      var metaCustomerTags = document.querySelector('meta[name="customer-tags"]');
      if (metaCustomerTags && metaCustomerTags.content) {
        return metaCustomerTags.content.split(',').map(function(tag) {
          return tag.trim();
        });
      }
      return [];
    },

    /**
     * Get promotion type from customer role
     * @param {string} role - Customer role
     * @returns {string|null} Promotion type or null
     */
    getPromotionType: function(role) {
      return Config.ROLE_TO_TYPE[role.toLowerCase()] || null;
    },

    /**
     * Get handler name from customer role
     * @param {string} role - Customer role
     * @returns {string|null} Handler name or null
     */
    getHandlerName: function(role) {
      return Config.ROLE_TO_HANDLER[role.toLowerCase()] || null;
    },

    /**
     * Check if SF Express is enabled for this promotion type
     * @param {string} promotionType - Promotion type
     * @returns {boolean} True if SF Express is enabled
     */
    isSFExpressEnabled: function(promotionType) {
      if (!Config.SF_EXPRESS.ENABLED) return false;
      return Config.SF_EXPRESS.EXCLUDED_ROLES.indexOf(promotionType) === -1;
    }
  };

  /**
   * Cart Manager - handles selection state and usage tracking
   * @namespace GniibPromotions.CartManager
   */
  GP.CartManager = {

    /**
     * Create a new cart manager instance
     * @returns {Object} Cart manager instance with selectedItems Map
     */
    create: function() {
      return {
        selectedItems: new Map(),
        promotionData: null,
        productsData: [],
        promotionType: null
      };
    },

    /**
     * Get item from selection by key
     * @param {Object} cart - Cart instance
     * @param {string} key - Item key (productId-variantId)
     * @returns {Object|undefined} Selected item or undefined
     */
    getItem: function(cart, key) {
      return cart.selectedItems.get(key);
    },

    /**
     * Set item in selection
     * @param {Object} cart - Cart instance
     * @param {string} key - Item key
     * @param {Object} item - Item data
     */
    setItem: function(cart, key, item) {
      cart.selectedItems.set(key, item);
    },

    /**
     * Remove item from selection
     * @param {Object} cart - Cart instance
     * @param {string} key - Item key
     */
    removeItem: function(cart, key) {
      cart.selectedItems.delete(key);
    },

    /**
     * Check if cart has items
     * @param {Object} cart - Cart instance
     * @returns {boolean} True if cart has items
     */
    hasItems: function(cart) {
      return cart.selectedItems.size > 0;
    },

    /**
     * Get total quantity of all selected items
     * @param {Object} cart - Cart instance
     * @returns {number} Total quantity
     */
    getTotalQuantity: function(cart) {
      var total = 0;
      cart.selectedItems.forEach(function(item) {
        total += item.quantity;
      });
      return total;
    },

    /**
     * Get quantity selected for a specific product
     * @param {Object} cart - Cart instance
     * @param {string|number} productId - Product ID
     * @returns {number} Quantity selected for product
     */
    getQuantityForProduct: function(cart, productId) {
      var total = 0;
      cart.selectedItems.forEach(function(item) {
        if (item.productId == productId) {
          total += item.quantity;
        }
      });
      return total;
    },

    /**
     * Get quantity selected for a specific variant
     * @param {Object} cart - Cart instance
     * @param {string|number} variantId - Variant ID
     * @returns {number} Quantity selected for variant
     */
    getQuantityForVariant: function(cart, variantId) {
      var total = 0;
      cart.selectedItems.forEach(function(item) {
        if (item.variantId == variantId) {
          total += item.quantity;
        }
      });
      return total;
    },

    /**
     * Get product usage from server data (for ibd_group backward compatibility)
     * @param {Object} cart - Cart instance
     * @param {string|number} productId - Product ID
     * @returns {number} Usage count from server
     */
    getProductUsageFromServer: function(cart, productId) {
      if (cart.promotionType !== 'ibd_group' || !cart.promotionData?.usage?.perProduct) {
        return 0;
      }

      var product = cart.productsData.find(function(p) { return p.id == productId; });
      if (!product) return 0;

      var perProductData = cart.promotionData.usage.perProduct;
      var usageData = perProductData[product.handle] || perProductData[productId];

      return usageData?.used || 0;
    },

    /**
     * Get variant usage from server data
     * Supports: ibd_group, partner_staff, liuyang, cumc
     * @param {Object} cart - Cart instance
     * @param {string|number} variantId - Variant ID
     * @returns {number} Usage count from server
     */
    getVariantUsageFromServer: function(cart, variantId) {
      var validTypes = ['ibd_group', 'partner_staff', 'liuyang', 'cumc'];
      if (validTypes.indexOf(cart.promotionType) === -1 || !cart.promotionData?.usage?.perVariant) {
        return 0;
      }

      var numericVariantId = Utils.extractVariantId(variantId);

      // Find product and variant to build composite key
      var product = null;
      var variant = null;

      for (var i = 0; i < cart.productsData.length; i++) {
        var p = cart.productsData[i];
        var v = p.variants.find(function(v) {
          return v.id == numericVariantId || parseInt(v.id) === numericVariantId;
        });
        if (v) {
          product = p;
          variant = v;
          break;
        }
      }

      var perVariantData = cart.promotionData.usage.perVariant;

      // Try composite key first
      if (product && variant) {
        var compositeKey = Utils.createVariantKey(product, variant);
        var usageData = perVariantData[compositeKey];
        if (usageData !== undefined) {
          return typeof usageData === 'number' ? usageData : (usageData.used || 0);
        }
      }

      // Fallback: direct variant ID lookup
      var fallbackData = perVariantData[numericVariantId] ||
                         perVariantData['gid://shopify/ProductVariant/' + numericVariantId];

      return typeof fallbackData === 'number' ? fallbackData : (fallbackData?.used || 0);
    },

    /**
     * Get remaining discounted units for a variant
     * @param {Object} cart - Cart instance
     * @param {string|number} variantId - Variant ID
     * @returns {number} Remaining discounted units
     */
    getRemainingForVariant: function(cart, variantId) {
      if (cart.promotionType !== 'ibd_group') return 0;

      var perVariantLimit = cart.promotionData?.promotion?.perVariantLimit || 3;
      var alreadyUsed = this.getVariantUsageFromServer(cart, variantId);
      var selectedInCart = this.getQuantityForVariant(cart, variantId);

      return Math.max(0, perVariantLimit - alreadyUsed - selectedInCart);
    },

    /**
     * Find product in cart data by ID
     * @param {Object} cart - Cart instance
     * @param {string|number} productId - Product ID
     * @returns {Object|null} Product or null
     */
    findProduct: function(cart, productId) {
      return cart.productsData.find(function(p) { return p.id == productId; }) || null;
    },

    /**
     * Find variant in product by ID
     * @param {Object} product - Product object
     * @param {string|number} variantId - Variant ID
     * @returns {Object|null} Variant or null
     */
    findVariant: function(product, variantId) {
      return product.variants.find(function(v) { return v.id == variantId; }) || null;
    },

    /**
     * Transform selected items to cart lines for checkout
     * Matches original format expected by server
     * @param {Object} cart - Cart instance
     * @param {Function} getDiscountName - Function to get discount name for item (unused, kept for compatibility)
     * @returns {Array} Cart lines array
     */
    transformToCartLines: function(cart, getDiscountName) {
      var cartLines = [];
      cart.selectedItems.forEach(function(item) {
        cartLines.push({
          merchandise: {
            product: {
              id: item.productId
            },
            id: item.variantId,
            price: {
              amount: (item.basePrice / 100).toFixed(2)
            }
          },
          quantity: item.quantity
        });
      });
      return cartLines;
    },

    /**
     * Calculate subtotal of all selected items
     * @param {Object} cart - Cart instance
     * @param {Function} calculateItemTotal - Function to calculate item total
     * @returns {number} Subtotal in cents
     */
    calculateSubtotal: function(cart, calculateItemTotal) {
      var subtotal = 0;
      cart.selectedItems.forEach(function(item) {
        subtotal += calculateItemTotal(item);
      });
      return subtotal;
    },

    /**
     * Iterate over selected items
     * @param {Object} cart - Cart instance
     * @param {Function} callback - Callback function(item, key)
     */
    forEach: function(cart, callback) {
      cart.selectedItems.forEach(callback);
    },

    /**
     * Clear all selected items
     * @param {Object} cart - Cart instance
     */
    clear: function(cart) {
      cart.selectedItems.clear();
    }
  };

})(window);
